import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { ThemeProvider } from './theme/ThemeProvider';
import AuthErrorView from './components/AuthErrorView';
import LoadingSplash from './components/LoadingSplash';
import MainLayout from './components/layout/MainLayout';
import AdminPage from './pages/AdminPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import StaticProjectPage from './pages/StaticProjectPage';
import UpdatesPage from './pages/UpdatesPage';
import UserHistoryPage from './pages/UserHistoryPage';
import UsersPage from './pages/UsersPage';
import WikiPage from './pages/WikiPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function AuthedShell() {
  return (
    <MainLayout>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/users/:email/history" element={<UserHistoryPage />} />
        <Route path="/product-wiki/wiki/*" element={<WikiPage section="wiki" />} />
        <Route path="/product-wiki/raw/*" element={<WikiPage section="raw" />} />
        <Route path="/product-wiki/updates" element={<UpdatesPage />} />
        {/* Catch-all by slug. StaticProjectPage redirects to / if the slug isn't a static sub-project. */}
        <Route path="/:slug/*" element={<StaticProjectPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </MainLayout>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading, authError } = useAuth();
  if (isLoading) return <LoadingSplash />;
  if (authError) return <AuthErrorView message={authError} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/*" element={isAuthenticated ? <AuthedShell /> : <Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
